import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ThumbDown,
  ThumbDownOutlined,
  ThumbUp,
  ThumbUpOutlined,
} from "@mui/icons-material";
import { useSendAiFeedbackMutation } from "src/_api/aiSupportApi";
import {
  ERROR_CODES,
  FEEDBACK_RATINGS,
} from "src/_modules/AiSupportChat/core/protocol";
import {
  PLACEHOLDER_SX,
  focusRingSx,
} from "src/_modules/AiSupportChat/constants";

/** Backend limit of the 👎 comment (plan §2.1). */
const COMMENT_MAX_CHARS = 500;

const { UP, DOWN } = FEEDBACK_RATINGS;

/** Our text for the codes a feedback request can meet, else the server's. */
const ERROR_KEYS = Object.freeze({
  [ERROR_CODES.NETWORK]: "errors.network",
  [ERROR_CODES.UNAVAILABLE]: "errors.unavailable",
  [ERROR_CODES.CONVERSATION_NOT_FOUND]: "errors.notFound",
});

const errorText = (error, t) => {
  const key = ERROR_KEYS[error?.code];
  if (key) return t(key);
  return error?.message || t("errors.generic");
};

/** A thumb: `aria-disabled` once rated, so focus stays on it (C31). */
const RatingButton = ({
  label,
  pressed,
  locked,
  onClick,
  children,
  ...props
}) => (
  <Tooltip title={label}>
    <IconButton
      size="small"
      aria-label={label}
      aria-pressed={pressed}
      aria-disabled={locked}
      onClick={locked ? undefined : onClick}
      sx={{
        color: pressed ? "primary.main" : "text.secondary",
        ...(locked && !pressed && { color: "action.disabled" }),
        ...(locked && { cursor: "default" }),
      }}
      {...props}
    >
      {children}
    </IconButton>
  </Tooltip>
);

/**
 * 👍 / 👎 under a complete answer (plan §3.6). 👍 saves at once; 👎 opens a
 * small popover with an optional comment (≤ 500 characters) and Send. Once
 * saved the chosen thumb stays filled and both buttons stop working (one
 * rating per answer), and «شكرًا لملاحظتك» is announced.
 *
 * The rating shown comes from the message (`feedback.rating`), so it is
 * there again when the conversation is reopened; `onSaved` puts a new one
 * into the chat state. Errors show next to the buttons (or in the popover)
 * and leave them usable.
 *
 * The buttons use `aria-disabled`, never `disabled`: the focused thumb
 * must not lose focus when the rating locks it (C31). The popover is an MUI
 * Modal, so Esc closes it, not the panel (D37), and focus returns to 👎.
 *
 * @param {object} props
 * @param {string} props.messageId the answer's server id
 * @param {"up" | "down" | null} [props.rating] saved rating, if any
 * @param {(feedback: { messageId: string, rating: string }) => void} props.onSaved
 */
export default function FeedbackButtons({ messageId, rating = null, onSaved }) {
  const { t, i18n } = useTranslation("aiSupport");
  const [sendFeedback] = useSendAiFeedbackMutation();
  const popoverId = useId();
  const counterId = useId();
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [thanked, setThanked] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [comment, setComment] = useState("");
  // Refuses a second request before the first render shows `pending`
  const pendingRef = useRef(false);

  const rated = rating === UP || rating === DOWN;
  const locked = rated || pending !== null;
  const popoverOpen = Boolean(anchorEl) && !rated;

  const save = (value, text) => {
    if (rated || pendingRef.current) return;
    pendingRef.current = true;
    setPending(value);
    setError("");
    sendFeedback({ messageId, rating: value, comment: text })
      .unwrap()
      .then(
        () => {
          setThanked(true);
          setAnchorEl(null);
          onSaved({ messageId, rating: value });
        },
        (failure) => setError(errorText(failure, t))
      )
      .finally(() => {
        pendingRef.current = false;
        setPending(null);
      });
  };

  const openComment = (event) => {
    setError("");
    setAnchorEl(event.currentTarget);
  };

  const closeComment = () => {
    // A request in flight still ends in the row (thanks or the error)
    setAnchorEl(null);
    if (!pendingRef.current) setError("");
  };

  const submitComment = (event) => {
    event.preventDefault();
    save(DOWN, comment);
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}
    >
      <RatingButton
        label={t("feedback.up")}
        pressed={rating === UP}
        locked={locked}
        aria-busy={pending === UP}
        onClick={() => save(UP)}
      >
        {rating === UP ? (
          <ThumbUp fontSize="small" />
        ) : (
          <ThumbUpOutlined fontSize="small" />
        )}
      </RatingButton>
      <RatingButton
        label={t("feedback.down")}
        pressed={rating === DOWN}
        locked={locked}
        aria-haspopup="dialog"
        aria-expanded={popoverOpen}
        aria-controls={popoverOpen ? popoverId : undefined}
        onClick={openComment}
      >
        {rating === DOWN ? (
          <ThumbDown fontSize="small" />
        ) : (
          <ThumbDownOutlined fontSize="small" />
        )}
      </RatingButton>

      {/* Mounted empty, so the thanks is announced when it appears */}
      <Typography
        role="status"
        variant="caption"
        color="text.secondary"
        sx={{ marginInlineStart: 0.5 }}
      >
        {thanked ? t("feedback.thanks") : ""}
      </Typography>
      {error && !popoverOpen && (
        <Typography role="alert" variant="caption" color="error">
          {error}
        </Typography>
      )}

      <Popover
        id={popoverId}
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={closeComment}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{
          role: "dialog",
          "aria-label": t("feedback.down"),
          // Portaled outside the panel: its own language and focus ring
          lang: i18n.language,
          sx: [
            { p: 1.5, width: 300, maxWidth: "calc(100vw - 32px)" },
            focusRingSx,
          ],
        }}
      >
        <Box component="form" noValidate onSubmit={submitComment}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            size="small"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={t("feedback.commentPlaceholder")}
            inputProps={{
              maxLength: COMMENT_MAX_CHARS,
              "aria-label": t("feedback.commentPlaceholder"),
              "aria-describedby": counterId,
              dir: "auto",
            }}
            sx={PLACEHOLDER_SX}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mt: 1,
            }}
          >
            <Typography id={counterId} variant="caption" color="text.secondary">
              {comment.length}/{COMMENT_MAX_CHARS}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              size="small"
              aria-disabled={pending !== null}
              startIcon={
                pending === DOWN ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ textTransform: "none" }}
            >
              {t("feedback.submit")}
            </Button>
          </Box>
          {error && (
            <Typography
              role="alert"
              variant="caption"
              color="error"
              component="p"
              sx={{ mt: 1 }}
            >
              {error}
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
